import React, { useEffect } from 'react'
import { Icon } from '@chakra-ui/icons'
import { FaPlus } from 'react-icons/fa6'

import { SetStateFn } from '~config/constants'
import { useFilePicker } from 'use-file-picker'
import { Attachment } from '~graphql/generated/graphql'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import { useToast } from '@chakra-ui/react'
import { getAttachmentType } from '~utils/helpers'
import { MAX_CHAT_ATTACHMENTS, useAddAttachments } from './use-add-attachments'

interface Props {
    chatId: string
    attachments: Attachment[]
    setAttachments: SetStateFn<Attachment[]>
    maxAttachments?: number
}

export default function AddAttachmentButton({
    chatId,
    attachments,
    setAttachments,
    maxAttachments = MAX_CHAT_ATTACHMENTS,
}: Props) {
    const toast = useToast()
    const { addFiles, isProcessing, isUploading } = useAddAttachments({
        chatId,
        setAttachments,
        maxAttachments,
    })

    const [
        openFileSelector,
        { filesContent, clear, loading: filesLoading, errors },
    ] = useFilePicker({
        multiple: true,
        accept: ['.pdf', 'image/*', 'video/*'],
        maxFileSize: 50,
        readAs: 'DataURL',
        limitFilesConfig: {
            min: 1,
            max: maxAttachments,
        },
    })

    useEffect(() => {
        if (!filesContent.length || isProcessing || filesLoading) return

        // Snapshot + clear first so this effect cannot re-fire on the same
        // selection when `addFiles`/processing state updates.
        const selected = filesContent.map(file => ({
            name: file.name,
            content: file.content,
            type: getAttachmentType(file.name),
        }))
        clear()
        void addFiles(selected)
    }, [filesContent, clear, addFiles, filesLoading, isProcessing])

    useEffect(() => {
        if (errors.some(e => e.maxLimitExceeded)) {
            toast({
                title: 'Error',
                description: `You can only attach ${maxAttachments} attachments at a time`,
                status: 'error',
                position: 'top',
            })
        }
    }, [errors])

    const isDisabled = attachments.length >= maxAttachments
    const isLoading = filesLoading || isUploading || isProcessing

    return (
        <IconButtonWithTooltip
            label="Add attachment"
            size="xs"
            w="28px"
            h="28px"
            rounded="full"
            variant="subtle"
            borderWidth={1}
            borderColor="gray.200"
            tooltipOverrides={{
                placement: 'top',
            }}
            icon={<Icon as={FaPlus} fontSize="sm" />}
            isLoading={isLoading}
            isDisabled={isDisabled || isLoading}
            onClick={() => openFileSelector()}
        />
    )
}
